/**
 * Role-aware Pandoy chat starters — aligned with auth showcase audiences.
 */
import {
  audienceFromSignupRole,
  pandoyQuestionForPlantation,
  showcaseForAudience,
  type AuthAudienceId,
  type AuthPlantationCrop,
} from './authFeatureShowcase';
import type { MonitoringArea } from '../types/auth';

export type PandoyStarter = {
  id: string;
  label: string;
  /** Sent to the agent as the user message */
  prompt: string;
};

export type LguMonitoringLevel = 'province' | 'municipality' | 'barangay';

function audienceIdFromUser(role?: string | null, subRole?: string | null): {
  audience: AuthAudienceId;
  crop: AuthPlantationCrop;
} {
  const audience = audienceFromSignupRole(String(role || 'blo'));
  const crop: AuthPlantationCrop =
    String(subRole || '').toLowerCase() === 'coconut' ? 'coconut' : 'banana';
  return { audience, crop };
}

function normalizeLguLevel(
  area?: MonitoringArea | null,
  fallbackLevel?: string | null
): LguMonitoringLevel {
  const raw = String(area?.level || fallbackLevel || '')
    .trim()
    .toLowerCase();
  if (raw === 'province' || raw === 'barangay' || raw === 'municipality') {
    return raw;
  }
  // Infer from assigned codes when level is missing
  if (area?.barangay_id != null && String(area.barangay_id).trim()) return 'barangay';
  if (area?.city_id != null && String(area.city_id).trim()) return 'municipality';
  if (area?.province_id != null && String(area.province_id).trim()) return 'province';
  return 'municipality';
}

function lguPlaceLabel(
  level: LguMonitoringLevel,
  area?: MonitoringArea | null
): { noun: string; nameHint: string } {
  if (level === 'province') {
    const name = String(area?.province || '').trim();
    return { noun: 'province', nameHint: name };
  }
  if (level === 'barangay') {
    const name = String(area?.barangay || '').trim();
    return { noun: 'barangay', nameHint: name };
  }
  const name = String(area?.municipality || '').trim();
  return { noun: 'municipality', nameHint: name };
}

function lguStaffLabel(level: LguMonitoringLevel): string {
  if (level === 'province') return 'provincial staff';
  if (level === 'barangay') return 'barangay staff';
  return 'municipal staff';
}

function lguReportPlace(level: LguMonitoringLevel, nameHint: string, noun: string): string {
  if (!nameHint) return `our ${noun}`;
  if (level === 'province') return `${nameHint} province`;
  if (level === 'barangay') return nameHint;
  return nameHint;
}

function lguReportGrain(level: LguMonitoringLevel): string {
  if (level === 'province') {
    return (
      'as one province-wide briefing. Do not summarize municipality by municipality. ' +
      'A province can contain up to about 50 municipalities, so name at most 5 places, and only if the data supports them'
    );
  }
  if (level === 'barangay') return 'for this one barangay only';
  return (
    'as one municipality-wide briefing. Do not list every barangay. ' +
    'A municipality can contain dozens of barangays, and some cities more than 200, so name at most 5 barangays, and only if the data supports them'
  );
}

/** Empty-state chips when no field is selected on the map. */
export function pandoyRoleStarters(
  role?: string | null,
  subRole?: string | null,
  monitoringArea?: MonitoringArea | null
): PandoyStarter[] {
  const { audience, crop } = audienceIdFromUser(role, subRole);
  const showcase = showcaseForAudience(audience);
  const sharedList: PandoyStarter = {
    id: 'list-my-farms',
    label: audience === 'lgu' ? 'How many farms in my area?' : 'List my farms',
    prompt:
      audience === 'lgu'
        ? 'Call list_my_farms for my monitoring area. Lead with the total count only (e.g. “You have N farms in this area”). Do not list all farm names. At most mention 1–3 sample names if useful, then ask which farm I want to analyse next.'
        : 'Call list_my_farms. If I have a small number of farms, name them; if there are many, lead with the count only and do not dump the full list. Ask which farm_id I should analyse next.',
  };

  if (audience === 'blo') {
    return [
      sharedList,
      {
        id: 'blo-health',
        label: showcase.pandoy.question,
        prompt:
          'I have not selected a field on the map. Call list_my_farms, then for the first farm with geometry (or ask me which one) call get_field_health and explain how healthy the rice field looks in plain language.',
      },
      {
        id: 'blo-weather',
        label: 'Weather for my fields',
        prompt:
          'Call list_my_farms, pick a representative farm (or ask me), then get_current_weather and get_weather_forecast. Summarize impacts for rice farming today and the next few days.',
      },
      {
        id: 'blo-pest',
        label: 'Pest risk check',
        prompt:
          'Call list_my_farms, then get_farm_pest_risk for a farm I should care about (ask if unclear). Explain rice pest risk in plain words.',
      },
    ];
  }

  if (audience === 'plantation') {
    const cropQ = pandoyQuestionForPlantation(crop);
    return [
      sharedList,
      {
        id: 'plant-blocks',
        label: cropQ,
        prompt: `I manage a ${crop} plantation and have not selected a block. Call list_my_farms, summarize my farms/blocks, then call get_field_health on one or two farms and tell me which blocks need scouting or treatment first this week.`,
      },
      {
        id: 'plant-weather',
        label: 'Weather for the estate',
        prompt: `Call list_my_farms, then weather tools for a main farm. Summarize for ${crop} estate operations.`,
      },
      {
        id: 'plant-screen',
        label: 'What does my map show?',
        prompt:
          'Capture the current dashboard with capture_dashboard_screenshot (alone in that turn), then explain what I should notice for plantation management.',
      },
    ];
  }

  if (audience === 'lgu') {
    const level = normalizeLguLevel(monitoringArea);
    const { noun, nameHint } = lguPlaceLabel(level, monitoringArea);
    const placePhrase = nameHint ? `our ${noun} (${nameHint})` : `our ${noun}`;
    const reportPlace = lguReportPlace(level, nameHint, noun);
    const areaScope =
      level === 'province'
        ? 'province-wide'
        : level === 'barangay'
          ? 'barangay-wide'
          : 'town-wide';
    return [
      sharedList,
      {
        id: 'lgu-report',
        label: `Prepare this month's report for ${reportPlace}`,
        prompt:
          `I monitor ${reportPlace} (${level} area` +
          (nameHint && monitoringArea?.province ? `; province ${String(monitoringArea.province).trim()}` : '') +
          (monitoringArea?.municipality ? `; municipality ${String(monitoringArea.municipality).trim()}` : '') +
          `). Do not write the full report yet. Offer these two reports and ask which I need. ` +
          'Say what each one includes, in plain words, then stop.\n' +
          'Situation report (this month): how many farms are in my area; how the land is used; ' +
          'and, from one sample farm only, how green and moist the crop looks, today’s weather, and this quarter’s pest risk.\n' +
          'Planning report (for policy): the same farm count; the drought outlook for the coming months; ' +
          'pest risk in later quarters; and the next few days of weather. We do not have a long seasonal weather forecast beyond drought.\n' +
          `Keep each finished report to those points, ${lguReportGrain(level)}. ` +
          'Use only list_my_farms (count, not every name), ' +
          'and one sample farm for get_field_health, get_current_weather, get_weather_forecast, get_farm_pest_risk, and get_farm_cadi_drought. ' +
          'Do not call vegetation history, anomaly, insurance, or crop calendar unless I ask. ' +
          'Say the field detail is one sample, not the whole area. Say what you could not measure. ' +
          `Write for ${lguStaffLabel(level)}.`,
      },
      {
        id: 'lgu-drought',
        label: `What's the drought outlook for our ${noun}?`,
        prompt:
          `What's the drought outlook for ${placePhrase}? ` +
          'Use list_my_farms if useful for sample fields, get_farm_cadi_drought for a representative farm if available, ' +
          `and explain in briefing language for ${lguStaffLabel(level)}. ` +
          `Point to Multi-Temporal CADI maps for ${areaScope} forecast.`,
      },
      {
        id: 'lgu-screen',
        label: 'Explain my map view',
        prompt:
          `Capture the dashboard with capture_dashboard_screenshot, then explain the view in plain words for ${lguStaffLabel(level)}.`,
      },
    ];
  }

  // project_developer / carbon
  return [
    sharedList,
    {
      id: 'carbon-area',
      label: showcase.pandoy.question,
      prompt:
        'I am a carbon project developer. Help me describe a project area, what is still unknown, and what evidence I would need before any carbon claim. Do not call sample figures credits or verified reductions.',
    },
    {
      id: 'carbon-screen',
      label: 'What is on my map?',
      prompt:
        'Capture the dashboard screenshot and describe sustainability / project-area evidence visible on screen.',
    },
  ];
}

function fieldRef(farmId: string, farmName?: string): string {
  const name = farmName?.trim();
  return name ? `farm_id="${farmId}" (${name})` : `farm_id="${farmId}"`;
}

function blockMeasureNote(blockCount: number): string {
  if (blockCount > 1) {
    return (
      `This field has ${blockCount} separate blocks. Each block is its own geometry. ` +
      'Call get_field_health. When the result includes a blocks list, compare every block ' +
      '(Block 1, Block 2, and so on): which is greener, which is drier, and which needs attention first. ' +
      'Do not answer with one average as if the blocks were the same. ' +
      'Weather and pest risk are for the field location as a whole unless a tool returns per-block numbers.'
    );
  }
  return 'This field is one block. Answer for this field only.';
}

/** Four questions when a saved field is selected. Wording follows the signed-in role. */
export function pandoyFieldStarters(
  role?: string | null,
  subRole?: string | null,
  field?: { farmId: string; farmName?: string; blockCount?: number } | null
): PandoyStarter[] {
  if (!field?.farmId) return [];
  const { audience, crop } = audienceIdFromUser(role, subRole);
  const blocks = Math.max(1, Number(field.blockCount) || 1);
  const many = blocks > 1;
  const ref = fieldRef(field.farmId, field.farmName);
  const measure = blockMeasureNote(blocks);
  const id = field.farmId;

  if (audience === 'plantation') {
    return [
      {
        id: 'field-plant-compare',
        label: many
          ? 'Which block needs attention first?'
          : `How healthy is this ${crop} block today?`,
        prompt:
          `For ${ref}: ${measure} ` +
          `Call get_field_health with farm_id="${id}" and explain greenness and moisture for ${crop} management.`,
      },
      {
        id: 'field-plant-health',
        label: many
          ? 'Compare greenness and moisture on each block'
          : 'Does this block need scouting this week?',
        prompt:
          `For ${ref}: ${measure} ` +
          `Call get_field_health with farm_id="${id}". Say whether this ${crop} planting needs scouting or treatment this week.`,
      },
      {
        id: 'field-plant-weather',
        label: many
          ? "What's the weather for work across these blocks?"
          : "What's the weather for work on this block?",
        prompt:
          `For ${ref}: call get_current_weather and get_weather_forecast with farm_id="${id}". ` +
          `Summarize what the next few days mean for ${crop} estate work. ${measure}`,
      },
      {
        id: 'field-plant-change',
        label: many
          ? 'How has each block changed over the last month?'
          : `Has this ${crop} changed over the last month?`,
        prompt:
          `For ${ref}: ${measure} ` +
          `Call get_vegetation_history with farm_id="${id}" and index="ndvi" for the field trend, ` +
          'and use the per-block greenness from get_field_health when more than one block exists.',
      },
    ];
  }

  if (audience === 'lgu') {
    return [
      {
        id: 'field-lgu-look',
        label: many ? 'Compare the blocks on this farm' : 'How does this farm look this month?',
        prompt:
          `For ${ref}: ${measure} ` +
          `Call get_field_health with farm_id="${id}" and brief municipal or provincial staff in plain language.`,
      },
      {
        id: 'field-lgu-stress',
        label: many ? 'Which block looks most stressed?' : "What's the drought outlook for this farm?",
        prompt: many
          ? `For ${ref}: ${measure} Call get_field_health with farm_id="${id}" and name the block that looks most stressed.`
          : `For ${ref}: call get_farm_cadi_drought with farm_id="${id}" and explain the drought outlook for this farm. ${measure}`,
      },
      {
        id: 'field-lgu-pest',
        label: "What's the pest risk on this farm?",
        prompt:
          `For ${ref}: call get_farm_pest_risk with farm_id="${id}" for this quarter. ` +
          `Explain rice pest risk for local staff. ${measure}`,
      },
      {
        id: 'field-lgu-weather',
        label: "What's the weather at this farm this week?",
        prompt:
          `For ${ref}: call get_current_weather and get_weather_forecast with farm_id="${id}". ` +
          `Keep it short for an office briefing. ${measure}`,
      },
    ];
  }

  if (audience === 'project_developer') {
    return [
      {
        id: 'field-carbon-green',
        label: many ? 'Compare greenness across each block' : 'How green is this plot right now?',
        prompt:
          `For ${ref}: ${measure} ` +
          `Call get_field_health with farm_id="${id}" and describe vegetation evidence a project partner would use.`,
      },
      {
        id: 'field-carbon-change',
        label: many ? 'Which block has changed the most?' : 'Has vegetation on this plot changed?',
        prompt:
          `For ${ref}: ${measure} ` +
          `Call get_vegetation_history with farm_id="${id}" and index="ndvi", and use per-block greenness from get_field_health when blocks exist.`,
      },
      {
        id: 'field-carbon-weather',
        label: "What's the weather at this site?",
        prompt:
          `For ${ref}: call get_current_weather and get_weather_forecast with farm_id="${id}". ${measure}`,
      },
    ];
  }

  return [
    {
      id: 'field-blo-health',
      label: many ? 'Which block on this field looks healthiest?' : 'How healthy is this rice field today?',
      prompt:
        `For ${ref}: ${measure} ` +
        `Call get_field_health with farm_id="${id}" and explain greenness, moisture, and stress in plain language for a rice farmer.`,
    },
    {
      id: 'field-blo-moisture',
      label: many ? 'Compare moisture across the blocks' : "Will this week's weather affect this field?",
      prompt: many
        ? `For ${ref}: ${measure} Call get_field_health with farm_id="${id}" and compare moisture on each block.`
        : `For ${ref}: call get_current_weather and get_weather_forecast with farm_id="${id}". Say what the next few days mean for this rice field. ${measure}`,
    },
    {
      id: 'field-blo-pest',
      label: "What's the pest risk on this rice field?",
      prompt:
        `For ${ref}: call get_farm_pest_risk with farm_id="${id}". Explain rice pest risk in plain words. ${measure}`,
    },
    {
      id: 'field-blo-change',
      label: many
        ? "Will this week's weather affect these blocks?"
        : 'Has this rice crop changed over the last month?',
      prompt: many
        ? `For ${ref}: call get_current_weather and get_weather_forecast with farm_id="${id}". ${measure}`
        : `For ${ref}: call get_vegetation_history with farm_id="${id}" and index="ndvi". Summarize how the rice crop changed. ${measure}`,
    },
  ];
}

export function pandoyPlaceholder(
  role?: string | null,
  hasField?: boolean,
  monitoringArea?: MonitoringArea | null
): string {
  if (hasField) return 'Ask Pandoy AI anything about this field…';
  const { audience } = audienceIdFromUser(role);
  if (audience === 'lgu') {
    const { noun } = lguPlaceLabel(normalizeLguLevel(monitoringArea), monitoringArea);
    return `Ask about your ${noun}, farms, or maps…`;
  }
  if (audience === 'plantation') return 'Ask about your blocks or estate…';
  if (audience === 'project_developer') return 'Ask about your project area or evidence…';
  return 'Ask about your farms — Pandoy can list them for you…';
}
